import React from 'react';
import PropTypes from 'prop-types';

/**
 * Reusable Card component for consistent card styling with modern design
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} [props.title] - Optional card title
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.headerClassName] - Additional CSS classes for header
 * @param {string} [props.bodyClassName] - Additional CSS classes for body
 * @param {React.ReactNode} [props.headerRight] - Optional content for right side of header
 * @param {string} [props.icon] - Optional icon to display next to title
 * @param {boolean} [props.hoverable] - Whether to apply hover effects
 * @param {string} [props.accentColor] - Optional accent color (blue, green, purple, orange, red)
 * @returns {JSX.Element} Rendered component
 */
const Card = ({
  children,
  title,
  className = '',
  headerClassName = '',
  bodyClassName = '',
  headerRight,
  icon,
  hoverable = false,
  accentColor = 'blue'
}) => {
  const accentColors = {
    blue: 'border-blue-500',
    green: 'border-green-500',
    purple: 'border-purple-500',
    orange: 'border-orange-500',
    red: 'border-red-500'
  };
  
  const borderAccent = accentColors[accentColor] || accentColors.blue;
  
  return (
    <div 
      className={`
        bg-gray-800 shadow-lg rounded-lg overflow-hidden border border-gray-700 
        ${hoverable ? 'transition-all duration-300 hover:shadow-xl hover:shadow-blue-900/10 hover:border-gray-600 transform hover:-translate-y-1' : ''}
        ${title ? 'border-t-4 ' + borderAccent : ''}
        ${className}
      `}
    >
      {title && (
        <div className={`px-5 py-4 sm:px-6 border-b border-gray-700 ${headerClassName}`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              {icon && <span className="text-gray-300">{icon}</span>}
              <h3 className="text-lg font-medium leading-6 text-white font-sora">{title}</h3>
            </div>
            {headerRight && (
              <div className="flex items-center">{headerRight}</div>
            )}
          </div>
        </div>
      )}
      <div className={`px-5 py-5 sm:p-6 ${bodyClassName}`}>
        {children}
      </div>
    </div>
  );
};

Card.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  headerRight: PropTypes.node,
  icon: PropTypes.node,
  hoverable: PropTypes.bool,
  accentColor: PropTypes.oneOf(['blue', 'green', 'purple', 'orange', 'red'])
};

export default Card; 